import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { styled, alpha } from "@mui/material/styles";
import { Box, Drawer, Typography, Avatar, Chip } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Scrollbar } from "../../components/Scrollbar";
import { NavSection } from "../../components/NavSection";
import { superAdminUserNavConfig, userNavConfig, adminUserNavConfig } from "../NavConfig";
import { useAppSelector } from "app/hooks";
import { selectAuth } from "app/services/auth/authSlice";
import { accountPhotoURL } from "definitions/constant/misc";
import { getRole } from "Service/Service";
import { ROLE_COLORS, ROLE_LABELS } from "themes/colors";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const menuMapping = {
  superadmin: superAdminUserNavConfig,
  admin: adminUserNavConfig,
  user: userNavConfig,
  employee: adminUserNavConfig,
};

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

// ----------------------------------------------------------------------

interface ISidebar {
  isOpenSidebar: boolean;
  onCloseSidebar: any;
}

export function Sidebar({ isOpenSidebar, onCloseSidebar }: ISidebar) {
  const { pathname } = useLocation();
  const auth = useAppSelector(selectAuth);
  const isDesktop = useResponsive("up", "lg");
  const role = getRole();
  const accentColor = ROLE_COLORS[role] || ROLE_COLORS.admin;

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          component="img"
          src="static/logos/logo.png"
          alt="Jaman HP Gas"
          sx={{ width: 42, height: 42, flexShrink: 0 }}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} color="text.primary">
            Jaman HP Gas
          </Typography>

        </Box>
      </Box>

      {/* User Info */}
      <Box
        sx={{
          mx: 2,
          mt: 2,
          mb: 0.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: alpha(accentColor, 0.06),
          border: '1px solid',
          borderColor: alpha(accentColor, 0.14),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          src={accountPhotoURL}
          sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 15, flexShrink: 0 }}
        >
          {auth.user?.name?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {auth.user?.name}
          </Typography>
          <Chip
            label={ROLE_LABELS[role] || role}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              mt: 0.25,
              bgcolor: alpha(accentColor, 0.12),
              color: accentColor,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      </Box>

      {/* Navigation */}
      <NavSection navConfig={menuMapping[getRole() as keyof typeof menuMapping]} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'grey.200',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          Jaman HP Gas © {new Date().getFullYear()}
        </Typography>
      </Box>
    </Scrollbar>
  );

  const drawerProps = {
    PaperProps: {
      sx: {
        width: DRAWER_WIDTH,
        borderRight: 'none',
        boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
      },
    },
  };

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} {...drawerProps}>
          {renderContent}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer open variant="persistent" {...drawerProps}>
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
